import React, { useEffect, useState } from "react";
import api, { API, getAuthToken } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Download, FileArchive, MessageSquare, Search, Upload } from "lucide-react";
import { toast } from "sonner";
import { NOISE_SOURCES, STATUS_LABELS, STATUS_COLORS } from "@/lib/constants";

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export default function Reports() {
  const [zones, setZones] = useState([]);
  const [filters, setFilters] = useState({
    status: "all", zone: "all", noise_source: "all", date_from: "", date_to: "",
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [smsRows, setSmsRows] = useState([]);
  const [smsSearch, setSmsSearch] = useState("");
  const [importing, setImporting] = useState(false);
  const smsImportRef = React.useRef(null);

  useEffect(() => {
    api.get("/zones").then((r) => setZones(r.data)).catch(() => {});
    loadSms();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
      const { data } = await api.get("/complaints", { params });
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const loadSms = async () => {
    try { const { data } = await api.get("/reports/sms"); setSmsRows(data); } catch {}
  };

  const exportCSV = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params.set(k, v); });
    const token = getAuthToken();
    const res = await fetch(`${API}/dashboard/reports/export?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return toast.error("CSV export failed");
    downloadBlob(await res.blob(), "noise_complaints_report.csv");
  };

  const downloadComplaintZip = async (c) => {
    setBusyId(c.id);
    try {
      const res = await api.get(`/reports/complaint/${c.id}`, { responseType: "blob" });
      downloadBlob(new Blob([res.data], { type: "application/zip" }),
        `complaint-${c.reference || c.id}.zip`);
      toast.success(`Downloaded ${c.reference}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "ZIP download failed");
    } finally { setBusyId(null); }
  };

  const downloadSmsCsv = async () => {
    try {
      const res = await api.get("/reports/sms.csv", { responseType: "blob" });
      downloadBlob(new Blob([res.data], { type: "text/csv" }), "nagpur-sms-log.csv");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "SMS log CSV download failed");
    }
  };

  const importSmsCsv = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/reports/sms/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        `Imported ${data.imported} · duplicates skipped ${data.skipped_duplicates}` +
          (data.errors ? ` · errors ${data.errors}` : "")
      );
      await loadSms();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "CSV import failed");
    } finally {
      setImporting(false);
      if (smsImportRef.current) smsImportRef.current.value = "";
    }
  };

  const smsFiltered = smsRows.filter((r) => {
    if (!smsSearch) return true;
    const q = smsSearch.toLowerCase();
    return [r.complaint_ref, r.to, r.recipient_role, r.status, r.provider_message_id]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
  });

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <div className="overline text-slate-500 mb-1">Reports</div>
            <h1 className="font-display font-bold text-3xl tracking-tight">Complaint records &amp; audit exports</h1>
            <p className="text-slate-600 mt-1 text-sm">Filter complaints, download the full escalation report with attachments, and audit every SMS sent.</p>
          </div>
          <Button className="gap-2" onClick={exportCSV} data-testid="export-csv-btn">
            <Download className="w-4 h-4" /> Export filtered CSV
          </Button>
        </div>

        <Card>
          <CardHeader><CardTitle className="font-display text-lg">Filters</CardTitle></CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
              <div>
                <label className="text-xs font-medium mb-1 block text-muted-foreground uppercase tracking-wider">Status</label>
                <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
                  <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block text-muted-foreground uppercase tracking-wider">Zone</label>
                <Select value={filters.zone} onValueChange={(v) => setFilters((f) => ({ ...f, zone: v }))}>
                  <SelectTrigger data-testid="filter-zone"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {zones.map((z) => <SelectItem key={z.key} value={z.key}>{z.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block text-muted-foreground uppercase tracking-wider">Source</label>
                <Select value={filters.noise_source} onValueChange={(v) => setFilters((f) => ({ ...f, noise_source: v }))}>
                  <SelectTrigger data-testid="filter-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {NOISE_SOURCES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block text-muted-foreground uppercase tracking-wider">Date from</label>
                <Input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} data-testid="filter-date-from" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block text-muted-foreground uppercase tracking-wider">Date to</label>
                <Input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} data-testid="filter-date-to" />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={load} disabled={loading} data-testid="apply-filters-btn">
                {loading ? "Loading..." : "Apply filters"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="complaint-reports-card">
          <CardHeader>
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <FileArchive className="w-4 h-4 text-slate-700" /> Complaint Full Reports · ZIP with escalation trail + attachments
              <Badge variant="secondary" className="ml-2">{items.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b">
                    <th className="py-2 pr-4 overline text-slate-500">Ref</th>
                    <th className="py-2 pr-4 overline text-slate-500">Title</th>
                    <th className="py-2 pr-4 overline text-slate-500">Zone</th>
                    <th className="py-2 pr-4 overline text-slate-500">Level</th>
                    <th className="py-2 pr-4 overline text-slate-500">Status</th>
                    <th className="py-2 pr-4 overline text-slate-500">Filed</th>
                    <th className="py-2 pr-4 overline text-slate-500 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => (
                    <tr key={c.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`report-row-${c.id}`}>
                      <td className="py-3 pr-4 font-mono text-xs">{c.reference || c.id.slice(-6)}</td>
                      <td className="py-3 pr-4 max-w-[240px] truncate" title={c.title}>{c.title}</td>
                      <td className="py-3 pr-4">{c.zone_name}</td>
                      <td className="py-3 pr-4 uppercase text-xs">{c.current_level || "—"}</td>
                      <td className="py-3 pr-4"><Badge variant="outline" className={STATUS_COLORS[c.status]}>{STATUS_LABELS[c.status]}</Badge></td>
                      <td className="py-3 pr-4 text-xs">{c.created_at ? new Date(c.created_at).toLocaleString() : "—"}</td>
                      <td className="py-3 pr-4 text-right">
                        <Button
                          size="sm" variant="outline" className="gap-1.5"
                          disabled={busyId === c.id}
                          onClick={() => downloadComplaintZip(c)}
                          data-testid={`download-complaint-zip-${c.id}`}
                        >
                          <FileArchive className="w-3.5 h-3.5" /> {busyId === c.id ? "…" : "ZIP"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">No records</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              Each ZIP contains a printable HTML report (complaint details, current status, full escalation trail PS→ACP→DCP, entire timeline) plus every attachment referenced by the complaint (citizen photos/videos, PI attend photo, resolution proofs) in an <span className="font-mono">/attachments</span> folder.
            </p>
          </CardContent>
        </Card>

        <Card data-testid="sms-log-card" className="border-blue-200 bg-blue-50/20">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <CardTitle className="font-display text-lg flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-blue-700" /> SMS Delivery Log
                <Badge variant="secondary" className="ml-2">{smsRows.length}</Badge>
              </CardTitle>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <div className="relative w-full sm:w-64">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <Input placeholder="Search ref / phone / status" className="pl-9 h-9"
                    value={smsSearch} onChange={(e) => setSmsSearch(e.target.value)}
                    data-testid="sms-log-search-input" />
                </div>
                <Button variant="outline" size="sm" className="gap-2" onClick={loadSms} data-testid="sms-log-refresh-btn">Refresh</Button>
                <input
                  ref={smsImportRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => importSmsCsv(e.target.files?.[0])}
                  data-testid="sms-log-import-input"
                />
                <Button
                  variant="outline" size="sm" className="gap-2"
                  disabled={importing}
                  onClick={() => smsImportRef.current?.click()}
                  data-testid="sms-log-import-btn"
                  title="Import bulksmsservice.co.in DLR CSV to backfill history"
                >
                  <Upload className="w-3.5 h-3.5" /> {importing ? "Importing…" : "Import DLR CSV"}
                </Button>
                <Button size="sm" className="gap-2" onClick={downloadSmsCsv} data-testid="sms-log-csv-btn">
                  <Download className="w-3.5 h-3.5" /> Download CSV
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="border border-blue-200 bg-white rounded-md overflow-hidden">
              <div className="max-h-[420px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">When</th>
                      <th className="text-left px-3 py-2 font-medium">Ref</th>
                      <th className="text-left px-3 py-2 font-medium">To</th>
                      <th className="text-left px-3 py-2 font-medium">Role</th>
                      <th className="text-left px-3 py-2 font-medium">Status</th>
                      <th className="text-left px-3 py-2 font-medium">Provider ID / Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {smsFiltered.map((r) => (
                      <tr key={r.id} className="border-t border-blue-100 hover:bg-blue-50/40" data-testid={`sms-log-row-${r.id}`}>
                        <td className="px-3 py-2 text-xs whitespace-nowrap">{r.sent_at ? new Date(r.sent_at).toLocaleString() : "—"}</td>
                        <td className="px-3 py-2 font-mono text-xs">{r.complaint_ref || "—"}</td>
                        <td className="px-3 py-2 font-mono text-xs">{r.to || "—"}</td>
                        <td className="px-3 py-2 text-xs uppercase">{r.recipient_role}</td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className={
                            r.status === "sent" ? "bg-emerald-50 text-emerald-800 border-emerald-300" :
                            r.status === "failed" || r.status === "error" ? "bg-red-50 text-red-800 border-red-300" :
                            "bg-slate-50 text-slate-700 border-slate-300"
                          }>{r.status || "—"}</Badge>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-700 max-w-[280px] truncate" title={r.provider_message_id || r.error}>
                          {r.provider_message_id || r.error || "—"}
                        </td>
                      </tr>
                    ))}
                    {smsFiltered.length === 0 && (
                      <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-500 text-sm">No SMS activity yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              Every SMS attempt (PI + citizen) is persisted here. Statuses: <b>sent</b> (provider acknowledged), <b>failed</b> (provider rejected), <b>skipped</b> (no phone or template not yet DLT-approved), <b>error</b> (network / timeout).
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
